// @flow
import { of } from 'rxjs';
import { debounceTime, mergeMap, map, catchError } from 'rxjs/operators';
import update from 'immutability-helper';
import { setNewAlert } from '../../alert/duck';
import { ofType } from 'redux-observable';
import { endpoints, getReceptionOrdersApi, receiveOrderItemApi } from './api';

import type { HTTPError } from '../../../error';
import type { Observable } from 'rxjs';
import type { ShipmentOrder } from '../../../entities';

type ReceptionAction = {
  +type: string;
  +receptionOrders?: ShipmentOrder[];
  +order?: ShipmentOrder;
  +query?: { [key: string]: string };
  +error?: HTTPError;
  +orderId?: string;
  +orderItemId?: number;
};

export type ReceptionState = {
  +isFetchingReceptionOrders?: boolean;
  +receptionOrders?: ShipmentOrder[];
  +fetchReceptionOrdersError?: HTTPError;
  +isReceivingOrderItemMap: { [key: string]: boolean };
  +receiveOrderItemError?: HTTPError;
};

// Actions
const RECEPTION_FETCH_RECEPTION_ORDERS_REQUEST = 'RECEPTION_FETCH_RECEPTION_ORDERS_REQUEST';
const RECEPTION_FETCH_RECEPTION_ORDERS_SUCCESS = 'RECEPTION_FETCH_RECEPTION_ORDERS_SUCCESS';
const RECEPTION_FETCH_RECEPTION_ORDERS_ERROR = 'RECEPTION_FETCH_RECEPTION_ORDERS_ERROR';

const RECEPTION_RECEIVE_ORDER_ITEM_REQUEST = 'RECEPTION_RECEIVE_ORDER_ITEM_REQUEST';
const RECEPTION_RECEIVE_ORDER_ITEM_SUCCESS = 'RECEPTION_RECEIVE_ORDER_ITEM_SUCCESS';
const RECEPTION_RECEIVE_ORDER_ITEM_ERROR = 'RECEPTION_RECEIVE_ORDER_ITEM_ERROR';

const RECEPTION_RESET_STATE = 'RECEPTION_RESET_STATE';

// Actions creators
export function fetchReceptionOrders(query?: { [key: string]: string }): ReceptionAction {
  return {
    type: RECEPTION_FETCH_RECEPTION_ORDERS_REQUEST,
    query,
  };
}

export function fetchReceptionOrdersSuccess(receptionOrders: ShipmentOrder[]): ReceptionAction {
  return {
    type: RECEPTION_FETCH_RECEPTION_ORDERS_SUCCESS,
    receptionOrders,
  };
}

export function fetchReceptionOrdersErrorFn(error: HTTPError): ReceptionAction {
  return {
    type: RECEPTION_FETCH_RECEPTION_ORDERS_ERROR,
    error,
  };
}

export function receiveOrderItem(orderId: string, orderItemId: number): ReceptionAction {
  return {
    type: RECEPTION_RECEIVE_ORDER_ITEM_REQUEST,
    orderId,
    orderItemId,
  };
}

export function receiveOrderSuccess(order: ShipmentOrder): ReceptionAction {
  return {
    type: RECEPTION_RECEIVE_ORDER_ITEM_SUCCESS,
    order,
  };
}

export function receiveOrderItemErrorFn(orderId: string, error: HTTPError): ReceptionAction {
  return {
    type: RECEPTION_RECEIVE_ORDER_ITEM_ERROR,
    orderId,
    error,
  };
}

export function resetState(): ReceptionAction {
  return {
    type: RECEPTION_RESET_STATE,
  };
}

export const actions = {
  RECEPTION_FETCH_RECEPTION_ORDERS_REQUEST,
  RECEPTION_FETCH_RECEPTION_ORDERS_SUCCESS,
  RECEPTION_FETCH_RECEPTION_ORDERS_ERROR,
  RECEPTION_RESET_STATE,
  RECEPTION_RECEIVE_ORDER_ITEM_REQUEST,
  RECEPTION_RECEIVE_ORDER_ITEM_SUCCESS,
  RECEPTION_RECEIVE_ORDER_ITEM_ERROR,
  fetchReceptionOrders,
  fetchReceptionOrdersSuccess,
  fetchReceptionOrdersErrorFn,
  receiveOrderItem,
  receiveOrderSuccess,
  receiveOrderItemErrorFn,
  resetState,
};

export const initialState: ReceptionState = {
  isReceivingOrderItemMap: {},
};

// Reducer
export default function reducer(state: ReceptionState = initialState,
                                action: ReceptionAction): ReceptionState {
  switch (action.type) {
    case RECEPTION_FETCH_RECEPTION_ORDERS_REQUEST:
      return update(state, {
        $unset: ['fetchReceptionOrdersError', 'receptionOrders'],
        isFetchingReceptionOrders: { $set: true },
      });
    case RECEPTION_FETCH_RECEPTION_ORDERS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingReceptionOrders'],
        receptionOrders: { $set: action.receptionOrders },
      });
    case RECEPTION_FETCH_RECEPTION_ORDERS_ERROR:
      return update(state, {
        $unset: ['isFetchingReceptionOrders'],
        fetchReceptionOrdersError: { $set: action.error },
      });
    case RECEPTION_RECEIVE_ORDER_ITEM_REQUEST:
      const up: { [key: string]: any } = {
        $unset: ['receiveOrderItemError'],
      };
      if (action.orderId) {
        up.isReceivingOrderItemMap = {
          [action.orderId]: { $set: true },
        };
      }
      return update(state, up);
    case RECEPTION_RECEIVE_ORDER_ITEM_SUCCESS:
      const upd: { [key: string]: any } = {
        $unset: ['isReceivingOrderItemMap', 'receiveOrderItemError'],
      };
      if (state.receptionOrders && action.order) {
        const editedOrderId = action.order.id;
        const idx = state.receptionOrders.findIndex(o => o.id === editedOrderId);
        upd.receptionOrders = {
          [idx]: { $set: action.order },
        };
        upd.isReceivingOrderItemMap = {
          [editedOrderId]: { $set: false },
        };
      }
      return update(state, upd);
    case RECEPTION_RECEIVE_ORDER_ITEM_ERROR:
      const upd2: { [key: string]: any } = {
        receiveOrderItemError: { $set: action.error },
      };
      if (action.orderId) {
        upd2.isReceivingOrderItemMap = {
          [action.orderId]: { $set: false },
        };
      }
      return update(state, upd2);
    case RECEPTION_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchReceptionOrdersEpic(action$: Observable<ReceptionAction>): Observable<ReceptionAction> {
  return action$.pipe(
    ofType(RECEPTION_FETCH_RECEPTION_ORDERS_REQUEST),
    debounceTime(600),
    mergeMap(({ query }) => getReceptionOrdersApi({ ...query }).pipe(
      map(fetchReceptionOrdersSuccess),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchReceptionOrdersErrorFn(e),
            setNewAlert('courier-reception', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchReceptionOrdersErrorFn(e),
            setNewAlert('courier-reception', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchReceptionOrdersErrorFn(e));
      })),
    ));
}

export function receiveOrderItemEpic(action$: Observable<ReceptionAction>): Observable<ReceptionAction> {
  return action$.pipe(
    ofType(RECEPTION_RECEIVE_ORDER_ITEM_REQUEST),
    mergeMap(({ orderId, orderItemId }) =>
      receiveOrderItemApi(undefined, {
        route: endpoints.RECEIVE_ORDER_ITEM.replace(':orderItemId', orderItemId),
      }).pipe(
        map(receiveOrderSuccess),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(receiveOrderItemErrorFn(orderId, e),
              setNewAlert('courier-reception', 'error', undefined, 'httpErrors.fetch.500'),
            );
          } else if (e.code === 400) {
            return of(receiveOrderItemErrorFn(orderId, e),
              setNewAlert('courier-reception', 'error', undefined, 'httpErrors.fetch.400'),
            );
          }
          return of(receiveOrderItemErrorFn(orderId, e));
        })),
    ));
}



// WEBPACK FOOTER //
// ./src/modules/courier/recepetion/duck.js